export type PendingVoiceRecordingUploadsBySession = Map<
  string,
  Set<Promise<void>>
>;

export function getVoiceRecordingUploadSessionKey(voiceSessionId: string) {
  return voiceSessionId;
}

export function trackPendingVoiceRecordingUpload(args: {
  uploadsBySession: PendingVoiceRecordingUploadsBySession;
  voiceSessionId: string;
  upload: Promise<void>;
  onTrackerError?: (error: unknown) => void;
}) {
  const sessionKey = getVoiceRecordingUploadSessionKey(args.voiceSessionId);
  let uploads = args.uploadsBySession.get(sessionKey);
  if (!uploads) {
    uploads = new Set();
    args.uploadsBySession.set(sessionKey, uploads);
  }
  uploads.add(args.upload);

  void args.upload
    .catch(() => {
      // The upload itself is handled by the caller or by allSettled during
      // teardown; this observer exists to avoid a dangling rejection from the
      // tracker chain.
    })
    .finally(() => {
      const currentUploads = args.uploadsBySession.get(sessionKey);
      currentUploads?.delete(args.upload);
      if (currentUploads?.size === 0) {
        args.uploadsBySession.delete(sessionKey);
      }
    })
    .catch((error) => {
      try {
        args.onTrackerError?.(error);
      } catch {
        // Keep the tracker observer terminal even if the reporter fails.
      }
    });
}

export async function waitForPendingVoiceRecordingUploads(args: {
  uploadsBySession: PendingVoiceRecordingUploadsBySession;
  voiceSessionId: string;
}) {
  const sessionKey = getVoiceRecordingUploadSessionKey(args.voiceSessionId);

  while (true) {
    const pendingUploads = args.uploadsBySession.get(sessionKey);
    if (!pendingUploads || pendingUploads.size === 0) {
      args.uploadsBySession.delete(sessionKey);
      return;
    }

    await Promise.allSettled([...pendingUploads.values()]);
  }
}
